import type { IChecklistStepTemplate, IObserverChecklist } from '../../../interface/observer-checklist';
import type { IVesrOutcome } from '../../../interface/vesr';

/** The first step not yet recorded, in election-day order; null when every step is in. */
export const nextStepKey = (steps: IChecklistStepTemplate[], checklist?: IObserverChecklist | null): string | null =>
	steps.find((s) => !checklist?.steps?.[s.key])?.key ?? null;

export const recordedCount = (steps: IChecklistStepTemplate[], checklist?: IObserverChecklist | null): number =>
	steps.filter((s) => !!checklist?.steps?.[s.key]).length;

/** "HH:MM" in local time, for a time input. */
export const toTimeInput = (date: Date): string =>
	`${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

/**
 * Today's date at the given local "HH:MM", as an ISO string. A time later than now is taken to be
 * yesterday's: an observer recording "close of poll" after midnight means last evening, never
 * tomorrow.
 */
export const fromTimeInput = (value: string, now: Date = new Date()): string | undefined => {
	const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
	if (!match) return undefined;
	const at = new Date(now);
	at.setHours(Number(match[1]), Number(match[2]), 0, 0);
	if (at.getTime() > now.getTime()) at.setDate(at.getDate() - 1);
	return at.toISOString();
};

/** A recorded time for display: "09:12". */
export const shortTime = (iso?: string | null): string => {
	if (!iso) return '';
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? '' : toTimeInput(d);
};

/** What went wrong and what to do about it, in the interface's voice. Null when it went right. */
export const outcomeMessage = (outcome: IVesrOutcome<unknown>): string | null => {
	if (!outcome.reachable) return 'Not saved. Check your connection and save again.';
	const { httpStatus } = outcome;
	if (httpStatus >= 200 && httpStatus < 300) return null;
	if (httpStatus === 404) return 'You are not posted to this polling unit. Ask your observer desk to check your posting.';
	if (httpStatus === 503) return 'Your posting could not be confirmed right now. Save again in a minute.';
	if (httpStatus === 403) return 'Checklists are not switched on for your account yet. Your observer desk can confirm.';
	if (httpStatus === 429) return `Too many saves at once. Save again in ${outcome.retryAfterSeconds ?? 60} seconds.`;
	if (httpStatus === 400) return outcome.errorMessage || 'This step could not be saved as entered.';
	return 'Not saved. Save again, and contact your observer desk if it keeps failing.';
};

/**
 * Loading the step template is not about any unit, so its 404 cannot mean "not posted here": it means
 * this VESR does not serve checklists yet (the route ships behind Vesr:ObserverReporting). Saying
 * "you are not posted" there sent observers to their desk about a posting that was fine.
 */
export const templateMessage = (outcome: IVesrOutcome<unknown>): string | null => {
	if (outcome.reachable && outcome.httpStatus === 404) {
		return 'The election-day checklist is not open yet. Your observer desk will tell you when it is.';
	}
	if (!outcome.reachable) return 'The checklist could not be loaded. Check your connection and try again.';
	return outcomeMessage(outcome);
};
